import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { updateRunbookApi, type UpdateRunBookStats } from './api'
import { useAuthStore } from '@/app/store/auth'
import type { ListParams } from '@/lib/api/types'

export const updateRunbookKeys = {
    all: ['update-runbook'] as const,
    lists: () => [...updateRunbookKeys.all, 'list'] as const,
    list: (params: ListParams) => [...updateRunbookKeys.lists(), params] as const,
}

export function useUpdateRunbook(params?: ListParams & { location?: string; team?: string }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: updateRunbookKeys.list(params || {}),
        queryFn: () => updateRunbookApi.list(params),
        enabled: isAuthenticated && !mfaRequired,
        placeholderData: keepPreviousData,
    })
}

export function useUpdateRunbookLocationOptions() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['update-runbook', 'filter-options', 'locations'],
        queryFn: async () => {
            const response = await updateRunbookApi.getLocationOptions()
            return response.data?.data || []
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useUpdateRunbookTeamOptions() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['update-runbook', 'filter-options', 'teams'],
        queryFn: async () => {
            const response = await updateRunbookApi.getTeamOptions()
            return response.data?.data || []
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useUpdateRunbookStats() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery<UpdateRunBookStats>({
        queryKey: ['update-runbook', 'stats'],
        queryFn: async () => {
            const response = await updateRunbookApi.getStats()
            const data = response.data?.data
            return data ? {
                ...data,
                runbookTaskCount: data.runbookTaskCount ?? 0,
                runbookTaskStarted: data.runbookTaskStarted ?? 0,
                runbookTaskCompleted: data.runbookTaskCompleted ?? 0,
                runbookTaskIssues: data.runbookTaskIssues ?? 0,
            } : { uniqueRequestCount: 0, objectCount: 0, locationCount: 0, runbookTaskCount: 0, runbookTaskStarted: 0, runbookTaskCompleted: 0, runbookTaskIssues: 0 }
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}
